import type { CloudEnvironment } from '../types/CloudEnvironment';
import type { CloudEnvironmentDiscoverer } from '../types/CloudEnvironmentDiscoverer';
import type { CloudEnvironmentResolver } from '../types/CloudEnvironmentResolver';
import type { CloudEnvironmentStorage } from '../types/CloudEnvironmentStorage';
import { getDomainFromEmailAddress } from '../utils/getDomainFromEmailAddress';
import { isSovereignEnvironment } from '../utils/isSovereignEnvironment';
import { getOrigin } from 'owa-url/lib/getOrigin';
import { isSovereignAppEnvironment } from '../api/isSovereignAppEnvironment';

export class CloudEnvironmentResolverImpl implements CloudEnvironmentResolver {
    private readonly activeDomainResolutions = new Map<string, Promise<CloudEnvironment>>();
    private readonly appLoadOrigin: string;

    constructor(
        private readonly storage: CloudEnvironmentStorage,
        private readonly getDiscoverer: () => Promise<CloudEnvironmentDiscoverer>
    ) {
        this.appLoadOrigin = getOrigin();
    }

    async resolveConfigForEmailAddress(emailAddress: string): Promise<CloudEnvironment> {
        const domain = getDomainFromEmailAddress(emailAddress);
        if (!domain) {
            throw new Error('Invalid email address: no domain found');
        }

        const activeDomainResolution = this.activeDomainResolutions.get(domain);
        if (activeDomainResolution) {
            return activeDomainResolution;
        }

        const domainResolution = this.resolveForDomainImpl(domain).finally(() =>
            this.activeDomainResolutions.delete(domain)
        ); // always clean up active resolutions, even if they fail

        this.activeDomainResolutions.set(domain, domainResolution);
        return domainResolution;
    }

    async refreshKnownConfigs(): Promise<void> {
        const domainsToRefresh = await this.storage.getDomainsToRefresh();
        if (domainsToRefresh.length === 0) {
            // Nothing to refresh: avoid loading the discovery subsystem.
            return;
        }

        const discoverer = await this.getDiscoverer();

        // Best-effort to refresh eligible domains; swallow any errors, they will
        // be logged within the discoverer
        await Promise.allSettled(
            domainsToRefresh.map(async domain => {
                const discovery = await discoverer.discoverConfigForDomain(domain);
                await this.storage.putConfigForDomain(
                    domain,
                    discovery.cloudEnvironment,
                    discovery.metadata
                );
            })
        );
    }

    private async resolveForDomainImpl(domain: string): Promise<CloudEnvironment> {
        const cloudEnv = await this.coordinateDomainLookup(domain);

        if (!isSovereignEnvironment(cloudEnv) && !isSovereignAppEnvironment()) {
            // If the app has bootstrapped from a non-sovereign environment and we're building a CloudEnvironment for
            // a non-sovereign environment, we should override the outlookWebOrigin to be the app bootstrap origin, rather than
            // the value from the discovery process. Why? There are several valid non-sovereign endpoints, and we should
            // align with the one the app loaded from to ensure all the (deprecated) usage of getOrigin aligns with the
            // cloud environment Outlook endpoint we use. Otherwise auth flows and routing might break.
            // Example scenario: config.json overrides baseUrl to https://outlook-sdf.office.com
            cloudEnv.outlookWebOrigin = this.appLoadOrigin;
        }

        return cloudEnv;
    }

    private async coordinateDomainLookup(domain: string): Promise<CloudEnvironment> {
        const cachedCloudEnv = await this.storage.getConfigForDomain(domain);
        if (cachedCloudEnv) {
            return cachedCloudEnv;
        }

        // Cache miss: lazily load the discovery subsystem (network HRD lookup) only now, so
        // cache-hit resolutions (e.g. boot-time auth for known domains) never pull the
        // discovery chunk.
        const discoverer = await this.getDiscoverer();
        const discovery = await discoverer.discoverConfigForDomain(domain);
        await this.storage.putConfigForDomain(
            domain,
            discovery.cloudEnvironment,
            discovery.metadata
        );

        return discovery.cloudEnvironment;
    }
}
